import { createSignal, type Accessor } from 'solid-js'
import { walkObjects, parseFontFamily } from '../canvas/utils'
import { resolveEffectiveStyle, type ThemeRecord } from '../project/themeVariables'
import type { RuntimeObject } from '../types'
import type { StyleValue } from '../project/posterSchema'
import { notifyFontLoaded } from '../canvas/text/fitText'

export const BUILTIN_FONTS = [
	'Roboto:wght@400;700;900',
	'Inter:wght@400;700',
	'Oswald:wght@400;700',
	'Anton',
	'Lora:wght@400;700',
]

export interface ParsedFont {
	spec: string
	family: string
	weights: number[]
	variable: boolean
}

/* Handles every css2 axis form: 'Anton', 'Inter:wght@400;700', 'Oswald:wght@200..700' and tuples
   such as 'Nunito+Sans:ital,opsz,wght@0,6..12,200..1000;1,6..12,200..1000'. A variable weight axis
   gives its two ends as the weights. */
export function parseSpec(spec: string): ParsedFont {
	const [namePart, axesPart] = spec.split(':')
	const family = namePart.replace(/\+/g, ' ')

	const [axisNames, tuples] = (axesPart ?? '').split('@')
	const wghtIndex = axisNames.split(',').indexOf('wght')
	if (tuples === undefined || wghtIndex < 0)
		return { spec, family, weights: [400], variable: false }

	const values   = tuples.split(';').map(tuple => tuple.split(',')[wghtIndex] ?? '')
	const variable = values.some(value => value.includes('..'))
	const numbers  = values.flatMap(value => value.split('..')).map(Number).filter(Boolean)
	const weights  = [...new Set(numbers)].sort((a, b) => a - b)

	return { spec, family, weights: weights.length > 0 ? weights : [400], variable }
}

export function weightsLabel(font: ParsedFont): string {
	return font.variable ? font.weights.join('–') : font.weights.join(', ')
}

export const BUILTIN_PARSED   = BUILTIN_FONTS.map(parseSpec)
export const BUILTIN_FAMILIES = new Set(BUILTIN_PARSED.map(f => f.family.toLowerCase()))

/* One font face a poster renders in: the family plus the weight and style that select which of the
   family's @font-face rules the browser actually paints with. The family alone is not enough to wait
   on — a design's default text is 700, and Google serves each weight as its own face that arrives
   separately (see posterFontsLoaded). */
export interface UsedFace {
	family: string
	weight: number
	style:  string
}

/* The faces the poster renders in, which are the ones to load and to wait on. Theme bindings are
   resolved for the same reason the renderer does it: a bound fontFamily renders in the variable's
   family, and the object's stored one is free to differ (see resolveEffectiveStyle). */
export function collectUsedFaces(objs: RuntimeObject[], theme: ThemeRecord|undefined): UsedFace[] {
	const byKey = new Map<string, UsedFace>()
	walkObjects(objs, obj => {
		if (obj.type === 'image') return
		const style = resolveEffectiveStyle(obj, theme)
		const ff    = style?.fontFamily
		if (typeof ff !== 'string') return
		const face = {
			family: parseFontFamily(ff),
			weight: normaliseWeight(style?.fontWeight),
			style:  normaliseFontStyle(style?.fontStyle),
		}
		byKey.set(`${face.family.toLowerCase()}|${face.weight}|${face.style}`, face)
	})
	return [...byKey.values()]
}

/* The families the poster renders in. Derived from collectUsedFaces so the loader and the readiness
   gate can never disagree about which families a design uses. */
export function collectUsedFamilies(objs: RuntimeObject[], theme: ThemeRecord|undefined): Set<string> {
	return new Set(collectUsedFaces(objs, theme).map(f => f.family))
}

/* A CSS font shorthand naming one face, for document.fonts.check()/load(). The size is arbitrary —
   font matching ignores it when selecting a face. */
export function faceDescriptor(face: UsedFace): string {
	return `${face.style} ${face.weight} 16px "${face.family}"`
}

/* These two must agree with measureWidth's defaults (see fitText), or the gate ends up waiting on a
   face that nothing ever measures or renders with. */
function normaliseWeight(value: StyleValue|undefined): number {
	if (typeof value === 'number') return Number.isFinite(value) ? value : 400
	if (typeof value !== 'string') return 400
	if (value === 'bold')   return 700
	if (value === 'normal') return 400
	const parsed = parseInt(value, 10)
	return Number.isFinite(parsed) ? parsed : 400
}

function normaliseFontStyle(value: StyleValue|undefined): string {
	return value === 'italic' || value === 'oblique' ? value : 'normal'
}

const FACE_RETRY_MS = 50

/* Ask the browser to load one specific face, and report whether it became usable.

   Two reasons this is explicit rather than left to layout. First, `display=swap` makes the browser
   fetch each weight lazily, so a face nothing has painted yet may never be requested at all. Second
   — and this is what makes it necessary rather than merely faster — the readiness gate only
   re-evaluates when fontEpoch ticks, and that is driven by `loadingdone`; a face that resolves
   without one leaves the gate stuck and the poster is never captured.

   An empty result means the family has no @font-face rule yet (the <link>'s CSS is still in flight),
   which is a retry rather than a failure. Bounded, unlike the font picker's original version, so a
   family that never arrives eventually stops asking. */
export async function requestFontFace(descriptor: string, attempts: number): Promise<boolean> {
	if (typeof document === 'undefined' || !document.fonts) return false

	for (let remaining = attempts; remaining > 0; remaining--) {
		if (await faceIsLoadable(descriptor)) {
			notifyFontLoaded()
			return true
		}
		await delay(FACE_RETRY_MS)
	}
	return false
}

// An unparseable descriptor throws rather than resolving empty; treat it as "not loadable" so a
// malformed family name can't turn into an unhandled rejection on every retry.
async function faceIsLoadable(descriptor: string): Promise<boolean> {
	try {
		return (await document.fonts.load(descriptor)).length > 0
	} catch {
		return false
	}
}

function delay(ms: number): Promise<void> {
	return new Promise(resolve => { setTimeout(resolve, ms) })
}

// The Google Fonts URL out of a pasted embed code ("<link href=...>"), as the family specifiers
// it carries. Null when the text holds no such URL.
export function parseGoogleFontsEmbed(text: string): string[] | null {
	const match = text.match(/https:\/\/fonts\.googleapis\.com\/css2\?[^\s"'<]+/)
	if (!match) return null
	try {
		const url = new URL(match[0])
		return url.searchParams.getAll('family').filter(Boolean)
	} catch {
		return null
	}
}

/* Every font specifier a poster built on this theme may load: the built-ins plus the theme's own,
   minus any theme spec that duplicates a built-in family. Split out of createFontState so the
   readiness gate — which is not a Solid component and has no FontContext — can ask the same
   question the loader answers. */
export function parsedFontsFor(themeFonts: string[]): ParsedFont[] {
	return [
		...BUILTIN_PARSED,
		...themeFonts.map(parseSpec).filter(f => !BUILTIN_FAMILIES.has(f.family.toLowerCase())),
	]
}

/* The families that arrive as web fonts, lowercased. Anything a design names outside this set is a
   system font: it needs no <link>, so there is nothing to wait for and nothing to embed. */
export function webFontFamilies(themeFonts: string[]): Set<string> {
	return new Set(parsedFontsFor(themeFonts).map(f => f.family.toLowerCase()))
}

export interface FontContext {
	fonts: Accessor<string[]>
	parsedFonts: () => ParsedFont[]
	preloadFamilies: Accessor<Set<string>>
	preloadFont: (family: string) => void
}

// The fonts a poster may use come from its theme (see PosterTheme's Fonts section) — a design
// carries none of its own, so this is read-only: it turns the theme's specifiers into the
// picker's list and tracks the families a control has asked to be preloaded.
export function createFontState(themeFonts: () => string[]): FontContext {
	const fonts: Accessor<string[]> = () => themeFonts()

	const parsedFonts = (): ParsedFont[] => parsedFontsFor(fonts())

	const [preloadFamilies, setPreloadFamilies] = createSignal(new Set<string>())
	function preloadFont(family: string) {
		setPreloadFamilies(prev => { const s = new Set(prev); s.add(family); return s })
	}

	return { fonts, parsedFonts, preloadFamilies, preloadFont }
}
